import React, { useState } from 'react';
import { getPhotoUrlWithFallback, useParticipants } from '@talkjs/react-components';
import moment from 'moment';
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Check, Close } from '@mui/icons-material';
import { Colors } from '../../../assets/theme/Colors';
import { renderUserRoleChip } from '../../../helpers/chat';
import { chatReopenModel, chatResolvedModel } from '../../../models/chat';

const chatHeaderButtonSx = {
  textDecoration: 'none',
  textTransform: 'none',
};

const SDCustomChatHeader = ({ common, session, onChatClose }) => {
  const { conversation, currentUser } = common;
  const participants = useParticipants(conversation.id, 30);
  const [dialogOpen, setDialogOpen] = useState(false);
  const firstParticipant = participants.find((p) => p?.user?.id === conversation?.custom?.createdBy)?.user;
  const firstParticipantRoles = firstParticipant?.custom?.roles ? JSON.parse(firstParticipant?.custom?.roles) : [];
  const firstParticipantTitles = firstParticipant?.custom?.titles ? JSON.parse(firstParticipant?.custom?.titles) : [];
  const isResolved = conversation?.custom?.resolved === 'true';
  const resolvedBy = conversation?.custom?.resolvedBy;
  const resolvedAt = conversation?.custom?.resolvedAt;

  const handleResolve = () => {
    session.conversation(conversation.id).set(chatResolvedModel(currentUser));

    onChatClose();
  };

  const handleReopen = () => {
    session.conversation(conversation.id).set(chatReopenModel(currentUser));

    onChatClose();
  };

  const handleManageUsers = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleRemoveUser = (id) => {
    session.conversation(conversation.id).participant(id).delete();
  };

  return (
    <>
      <Box
        sx={{
          minHeight: 70,
          display: 'flex',
          direction: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar
            sx={{ width: 40, height: 40 }}
            alt={firstParticipant?.name}
            src={firstParticipant ? getPhotoUrlWithFallback(firstParticipant) : 'no-picture'}
          />
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {firstParticipant?.name}
              </Typography>
              {Array.isArray(firstParticipantRoles) && firstParticipantRoles.map((role) => renderUserRoleChip(role))}
            </Box>
            <Stack>
              {Array.isArray(firstParticipantTitles) && (
                <Typography variant="caption" color="text.secondary">
                  {firstParticipantTitles.join(', ')}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                {participants?.length} people · {participants?.map((p) => p?.user?.name).join(', ')}
              </Typography>
            </Stack>
          </Box>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          {!isResolved && (
            <Button
              variant="contained"
              color="confirm"
              size="small"
              sx={chatHeaderButtonSx}
              startIcon={<Check />}
              onClick={handleResolve}
            >
              Mark resolved
            </Button>
          )}
          <Button variant="outlined" size="small" sx={chatHeaderButtonSx} onClick={handleManageUsers}>
            Manage people
          </Button>
        </Stack>
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: Colors.chat.selected,
          px: 2,
          py: 0.5,
        }}
      >
        {!isResolved && (
          <>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <Box sx={{ bgcolor: 'confirm.main', p: 0.5, borderRadius: '50%' }} />
              <Typography variant="subtitle2" color="text.primary" sx={{ fontWeight: 700 }}>
                Open
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Status is shared across all site staff
            </Typography>
          </>
        )}
        {isResolved && (
          <>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <Check sx={{ color: 'confirm.main', fontSize: 12 }} />
              <Typography variant="subtitle2" color="confirm.main" sx={{ fontWeight: 700 }}>
                Resolved by {resolvedBy} {moment(Number(resolvedAt)).fromNow()}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              onClick={handleReopen}
              sx={[
                chatHeaderButtonSx,
                {
                  fontSize: '12px !important',
                  color: 'text.secondary',
                  borderColor: 'text.secondary',
                  bgcolor: 'common.white',
                },
              ]}
            >
              Reopen
            </Button>
          </>
        )}
      </Box>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <Box>
          <DialogTitle sx={{ display: 'flex', flexDirection: 'column' }}>
            Manage people
            <Typography variant="caption" color="text.secondary">
              Visible to site directors & coordinators only - never to initiator or other members
            </Typography>
          </DialogTitle>
        </Box>
        <IconButton
          onClick={handleCloseDialog}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'text.secondary',
          }}
        >
          <Close />
        </IconButton>
        <DialogContent dividers>
          {participants.map((p, index) => {
            const lastItem = index + 1 < participants.length;
            const isMe = p.user.id === currentUser.id;
            const userRoles = p?.user?.custom?.roles ? JSON.parse(p?.user?.custom?.roles) : [];
            const userTitles = p?.user?.custom?.titles ? JSON.parse(p?.user?.custom?.titles) : [];

            return (
              <Box sx={{ pb: lastItem ? 2 : 0 }} key={p?.user?.id}>
                <Box sx={{ pb: lastItem ? 2 : 0, display: 'flex', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Avatar sx={{ width: 40, height: 40 }} alt={p?.user?.name} src={getPhotoUrlWithFallback(p?.user)} />
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 0.8 }}>
                          {p?.user?.name}
                        </Typography>
                        {Array.isArray(userRoles) && userRoles.map((role) => renderUserRoleChip(role))}
                        {isMe && (
                          <Typography variant="caption" color="text.secondary">
                            You
                          </Typography>
                        )}
                        {p?.user?.id === firstParticipant?.id && (
                          <Typography variant="caption" color="text.secondary">
                            Started this chat
                          </Typography>
                        )}
                      </Box>
                      {Array.isArray(userTitles) && (
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {userTitles.join(', ')}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Button
                    variant="outlined"
                    sx={chatHeaderButtonSx}
                    disabled={p?.user?.id === firstParticipant?.id}
                    color="cancel"
                    onClick={() => handleRemoveUser(p?.user?.id)}
                  >
                    Remove
                  </Button>
                </Box>
                {lastItem && <Divider sx={{ mx: -3 }} />}
              </Box>
            );
          })}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SDCustomChatHeader;
